import { useEffect, useMemo, useState, type FormEvent } from 'react'
import { Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { MultiSelectFilter } from '@/components/campaigns/MultiSelectFilter'
import { compactFilters, DEFAULT_FILTERS, formatNumber } from '@/components/campaigns/campaignFormat'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Sheet, SheetContent, SheetDescription, SheetTitle } from '@/components/ui/sheet'
import { useParams } from 'react-router-dom'
import type { CampaignFilters, FilterOptions } from '@/services/crmCampaignsService'

const DEMAND_STATES = ['Activa', 'Pendiente']
const OPERATION_TYPES = ['Venta', 'Alquiler']
const EXTRAS = ['garage', 'elevator', 'terrace', 'pool']
const ROOM_OPTIONS = [1, 2, 3, 4]

interface AudienceFiltersProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  applied: CampaignFilters
  options: FilterOptions | undefined
  /** Los destinatarios que dan los filtros ya aplicados, si se conocen. */
  knownCount: number | null
  applying: boolean
  onApply: (filters: CampaignFilters) => void
}

const parseNumber = (value: string): number | null => (value === '' ? null : Number(value))
const asOptions = (values: string[] | undefined) => (values ?? []).map((value) => ({ value, label: value }))

/**
 * Los filtros de la audiencia, en un panel sobre lo que ya se estaba viendo.
 *
 * El botón dice cuántos saldrán, como pide la guía (§5.2), pero solo cuando lo
 * sabe: calcular una audiencia sustituye los destinatarios del borrador y encola
 * la consulta de consentimiento, así que no se recalcula con cada tecla. Tras
 * cambiar algo, el número de antes ya no sería cierto y deja de enseñarse.
 */
export function AudienceFilters({ open, onOpenChange, applied, options, knownCount, applying, onApply }: AudienceFiltersProps) {
  const { t } = useTranslation()
  const { lang = 'es' } = useParams()
  const [draft, setDraft] = useState<CampaignFilters>(applied)

  useEffect(() => {
    if (open) setDraft(applied)
  }, [open, applied])

  const set = <K extends keyof CampaignFilters>(key: K, value: CampaignFilters[K]) =>
    setDraft((prev) => ({ ...prev, [key]: value }))

  const toggleIn = (key: 'estados' | 'tipos_operacion' | 'extras', value: string, checked: boolean) => {
    const current = draft[key] ?? []
    set(key, checked ? [...current, value] : current.filter((item) => item !== value))
  }

  const unchanged = useMemo(
    () => JSON.stringify(compactFilters(draft)) === JSON.stringify(compactFilters(applied)),
    [draft, applied],
  )
  const daysLocked = options?.gestiones_cargadas === false
  const zones = options?.zonas

  const submit = (event: FormEvent) => {
    event.preventDefault()
    onApply(compactFilters(draft))
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full overflow-y-auto p-6 sm:max-w-md">
        <div className="space-y-1">
          <SheetTitle>{t('campaigns.filters.title')}</SheetTitle>
          <SheetDescription>{t('campaigns.filters.description')}</SheetDescription>
        </div>

        <form className="flex flex-1 flex-col gap-4 py-4" onSubmit={submit} noValidate>
          <fieldset className="space-y-2">
            <legend className="text-sm font-medium">{t('campaigns.filters.demand_state')}</legend>
            <div className="flex flex-wrap gap-4">
              {DEMAND_STATES.map((state) => (
                <Label key={state} className="flex items-center gap-2 font-normal">
                  <Checkbox
                    aria-label={t(`campaigns.filters.state_values.${state}`)}
                    checked={(draft.estados ?? []).includes(state)}
                    onCheckedChange={(checked) => toggleIn('estados', state, checked === true)}
                  />
                  {t(`campaigns.filters.state_values.${state}`)}
                </Label>
              ))}
            </div>
          </fieldset>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="filter-days-min">{t('campaigns.filters.days_min')}</Label>
              <Input
                id="filter-days-min"
                aria-label={t('campaigns.filters.days_min')}
                type="number"
                min={0}
                disabled={daysLocked}
                value={draft.dias_sin_gestion_min ?? ''}
                onChange={(event) => set('dias_sin_gestion_min', parseNumber(event.target.value))}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="filter-days-max">{t('campaigns.filters.days_max')}</Label>
              <Input
                id="filter-days-max"
                aria-label={t('campaigns.filters.days_max')}
                type="number"
                min={0}
                disabled={daysLocked}
                value={draft.dias_sin_gestion_max ?? ''}
                onChange={(event) => set('dias_sin_gestion_max', parseNumber(event.target.value))}
              />
            </div>
            {daysLocked ? (
              <p className="text-xs text-muted-foreground sm:col-span-2">{t('campaigns.filters.days_locked')}</p>
            ) : null}
          </div>

          <MultiSelectFilter
            id="filter-provinces"
            label={t('campaigns.filters.provinces')}
            options={asOptions(options?.provincias)}
            selected={draft.provincias ?? []}
            onChange={(value) => set('provincias', value)}
          />
          <MultiSelectFilter
            id="filter-municipalities"
            label={t('campaigns.filters.municipalities')}
            options={asOptions(options?.municipios)}
            selected={draft.municipios ?? []}
            onChange={(value) => set('municipios', value)}
          />
          <MultiSelectFilter
            id="filter-towns"
            label={t('campaigns.filters.towns')}
            options={asOptions(options?.poblaciones)}
            selected={draft.poblaciones ?? []}
            onChange={(value) => set('poblaciones', value)}
          />

          {/* Sin zonas es que la web no ha respondido: lo demás sigue funcionando. */}
          {zones ? (
            <div className="space-y-1">
              <MultiSelectFilter
                id="filter-zones"
                label={t('campaigns.filters.zones')}
                options={zones.map((zone) => ({
                  value: String(zone.id),
                  label: zone.municipio ? `${zone.nombre} (${zone.municipio})` : zone.nombre,
                }))}
                selected={(draft.zonas ?? []).map((zone) => String(zone.id))}
                onChange={(ids) =>
                  set(
                    'zonas',
                    zones
                      .filter((zone) => ids.includes(String(zone.id)))
                      .map((zone) => ({ id: zone.id, nombre: zone.nombre })),
                  )
                }
              />
              <p className="text-xs text-muted-foreground">{t('campaigns.filters.zones_help')}</p>
            </div>
          ) : null}

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="filter-price-min">{t('campaigns.filters.price_min')}</Label>
              <Input
                id="filter-price-min"
                aria-label={t('campaigns.filters.price_min')}
                type="number"
                min={0}
                placeholder={options?.precio_min != null ? formatNumber(options.precio_min, lang) : undefined}
                value={draft.precio_hasta_min ?? ''}
                onChange={(event) => set('precio_hasta_min', parseNumber(event.target.value))}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="filter-price-max">{t('campaigns.filters.price_max')}</Label>
              <Input
                id="filter-price-max"
                aria-label={t('campaigns.filters.price_max')}
                type="number"
                min={0}
                placeholder={options?.precio_max != null ? formatNumber(options.precio_max, lang) : undefined}
                value={draft.precio_hasta_max ?? ''}
                onChange={(event) => set('precio_hasta_max', parseNumber(event.target.value))}
              />
            </div>
          </div>

          <MultiSelectFilter
            id="filter-agents"
            label={t('campaigns.filters.agents')}
            options={(options?.gestores ?? []).map((agent) => ({ value: agent.id, label: agent.nombre }))}
            selected={draft.gestores ?? []}
            onChange={(value) => set('gestores', value)}
          />

          <fieldset className="space-y-2">
            <legend className="text-sm font-medium">{t('campaigns.filters.operation')}</legend>
            <div className="flex flex-wrap gap-4">
              {OPERATION_TYPES.map((operation) => (
                <Label key={operation} className="flex items-center gap-2 font-normal">
                  <Checkbox
                    aria-label={t(`campaigns.filters.operation_values.${operation}`)}
                    checked={(draft.tipos_operacion ?? []).includes(operation)}
                    onCheckedChange={(checked) => toggleIn('tipos_operacion', operation, checked === true)}
                  />
                  {t(`campaigns.filters.operation_values.${operation}`)}
                </Label>
              ))}
            </div>
          </fieldset>

          <MultiSelectFilter
            id="filter-property-types"
            label={t('campaigns.filters.property_types')}
            options={asOptions(options?.tipos_inmueble)}
            selected={draft.tipos_inmueble ?? []}
            onChange={(value) => set('tipos_inmueble', value)}
          />

          {/* Opciones a la vista y no un contador: quien quiere 3 no tiene que pulsar tres veces. */}
          <fieldset className="space-y-2">
            <legend className="text-sm font-medium">{t('campaigns.filters.rooms')}</legend>
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                size="xs"
                variant={draft.habitaciones_min == null ? 'secondary' : 'outline'}
                aria-pressed={draft.habitaciones_min == null}
                onClick={() => set('habitaciones_min', null)}
              >
                {t('campaigns.filters.any')}
              </Button>
              {ROOM_OPTIONS.map((rooms) => (
                <Button
                  key={rooms}
                  type="button"
                  size="xs"
                  variant={draft.habitaciones_min === rooms ? 'secondary' : 'outline'}
                  aria-pressed={draft.habitaciones_min === rooms}
                  onClick={() => set('habitaciones_min', rooms)}
                >
                  {rooms}+
                </Button>
              ))}
            </div>
          </fieldset>

          <fieldset className="space-y-2">
            <legend className="text-sm font-medium">{t('campaigns.filters.extras')}</legend>
            <div className="grid grid-cols-2 gap-2">
              {EXTRAS.map((extra) => (
                <Label key={extra} className="flex items-center gap-2 font-normal">
                  <Checkbox
                    aria-label={t(`campaigns.filters.extra_values.${extra}`)}
                    checked={(draft.extras ?? []).includes(extra)}
                    onCheckedChange={(checked) => toggleIn('extras', extra, checked === true)}
                  />
                  {t(`campaigns.filters.extra_values.${extra}`)}
                </Label>
              ))}
            </div>
          </fieldset>

          <div className="mt-auto flex flex-row justify-between gap-2 pt-4">
            <Button type="button" variant="ghost" onClick={() => setDraft(DEFAULT_FILTERS)}>
              {t('campaigns.filters.reset')}
            </Button>
            <Button type="submit" disabled={applying}>
              {applying ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              {unchanged && knownCount !== null
                ? t('campaigns.filters.apply_count', { count: formatNumber(knownCount, lang) })
                : t('campaigns.filters.apply')}
            </Button>
          </div>
        </form>
      </SheetContent>
    </Sheet>
  )
}
